import * as amplitude from "@amplitude/analytics-browser"
import { DEVICE_ID_COOKIE_FIELD } from "@finway-group/shared/lib/consts/common"
import React, { createContext, useContext, useEffect, useState } from "react"
import { Cookies } from "react-cookie"
import { useAuth } from "Shared/hooks/auth.hooks"
import { isNodeEnvProd } from "Shared/utils/helper.utils"

interface AnalyticsContextType {
    logEvent: (eventName: string, eventProperties: Record<string, any>) => void
}

const AnalyticsContext = createContext<AnalyticsContextType>({} as AnalyticsContextType)

const amplitudeMock = {
    init: () => {},
    track: () => {},
    groupIdentify: () => {},
    identify: () => {},
    reset: () => {},
    setDeviceId: () => {},
    setUserId: () => {},
    setGroup: () => {},
    setOptOut: (shouldOptOut: boolean) => {
        void shouldOptOut
    },
}

const isAmplitudeEnabled = process.env.REACT_APP_AMPLITUDE_API_KEY && isNodeEnvProd

// so we don't track anything in dev/local modes
const amplitudeInstance = isAmplitudeEnabled ? amplitude : amplitudeMock

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
    const [isInitialized, setIsInitialized] = useState(false)
    const { userId, userClass, companyId, isAuthenticated } = useAuth()
    const cookies = new Cookies(document.cookie)
    const deviceId = cookies.get(DEVICE_ID_COOKIE_FIELD)

    // Initialize amplitude
    useEffect(() => {
        if (!isInitialized) {
            // auto-caption will automatically capture events like page visits, form interactions, etc.
            amplitudeInstance.init(process.env.REACT_APP_AMPLITUDE_API_KEY || "", userId, {
                autocapture: true,
                serverZone: "EU",
                // start opted-out to avoid anonymous event tracking; will opt-in after auth
                optOut: !isAuthenticated,
            })
            setIsInitialized(true)
        }
    }, [])

    // Set user/group properties
    useEffect(() => {
        if (!isInitialized || (!companyId && !userId && !userClass)) {
            return
        }
        // Associate user with a company (group)
        if (companyId) {
            amplitudeInstance.setGroup("companyId", companyId)
            const groupIdentify = new amplitude.Identify()
            groupIdentify.set("tenantId", userClass?.tenantId || "null")
            amplitudeInstance.groupIdentify("company_id", companyId, groupIdentify)
        }
        if (userId) {
            amplitudeInstance.setUserId(userId)
        }
        if (userClass) {
            // Set user properties
            const identifyEvent = new amplitude.Identify()
            // disabling due to privacy restrictions
            // identifyEvent.set("name", userClass.name)
            // identifyEvent.set("email", userClass.email)
            amplitudeInstance.identify(identifyEvent)
        }
    }, [userId, userClass, companyId, isInitialized])

    useEffect(() => {
        if (!isInitialized) {
            return
        }
        if (!isAuthenticated) {
            amplitudeInstance.setOptOut(true)
            amplitudeInstance.reset()
        } else {
            amplitudeInstance.setOptOut(false)
        }
    }, [isAuthenticated, isInitialized])

    useEffect(() => {
        if (deviceId && isInitialized) {
            amplitudeInstance.setDeviceId(deviceId)
        }
    }, [deviceId, isInitialized])

    const logEvent = (eventName: string, eventProperties: Record<string, any>) => {
        amplitudeInstance.track(eventName, eventProperties)
    }

    return <AnalyticsContext.Provider value={{ logEvent }}>{children}</AnalyticsContext.Provider>
}

export const useAnalytics = () => useContext(AnalyticsContext)
